import { useState, useEffect } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DateTimePicker } from "./DateTimePicker";
import { ReminderStage } from "./types";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Eye, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

interface EmailTemplate {
  id: string;
  name: string;
  template_type: string;
  content: string;
}

type TemplateType = "initial" | "reminder_1" | "reminder_2" | "reminder_3" | "reminder_4";

interface ReminderStageSectionProps {
  title: string;
  stage: ReminderStage;
  templateType: TemplateType;
  onChange: (stage: ReminderStage) => void;
  previousStageDate?: Date | undefined;
  onInitialDateChange?: (date: Date) => void;
  useCurrentTimePlusFifteen?: boolean;
  invoiceId?: string;
  allowClear?: boolean;
  onClear?: () => void;
}

export const ReminderStageSection = ({
  title,
  stage,
  templateType,
  onChange,
  previousStageDate,
  onInitialDateChange,
  useCurrentTimePlusFifteen = false,
  invoiceId,
  allowClear = false,
  onClear,
}: ReminderStageSectionProps) => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const { t } = useTranslation();

  // Function to replace template variables with sample values
  const replaceTemplateVariables = (content: string) => {
    return content
      .replace(/{customer_name}/g, "John Smith")
      .replace(/{invoice_number}/g, "INV-2023-001")
      .replace(/{amount}/g, "€1,000.00")
      .replace(/{due_date}/g, "January 31, 2024")
      .replace(/{business_name}/g, "Happy Co.");
  };

  // Function to preview the selected template

  const handlePreviewClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!stage.templateId) {
      toast.error(t("schedule_dialog.no_template_selected", "Please select a template first"));
      return;
    }

    const template = templates.find(t => t.id === stage.templateId);
    if (template) {
      setSelectedTemplate(template);
      setPreviewOpen(true);
    }
  };

  useEffect(() => {
    const fetchTemplatesAndSchedule = async () => {
      try {
        // Fetch email templates
        const { data: templatesData, error: templatesError } = await supabase
          .from("email_templates")
          .select("*")
          .eq("template_type", templateType);

        if (templatesError) throw templatesError;
        setTemplates(templatesData || []);

        // If we have an invoice ID, fetch its schedule
        if (invoiceId) {
          const { data: scheduleData, error: scheduleError } = await supabase
            .from("invoice_schedules")
            .select("*")
            .eq("invoice_id", invoiceId)
            .maybeSingle();

          if (scheduleError) throw scheduleError;

          if (scheduleData) {
            let scheduleDate: Date | undefined;
            let templateId: string | undefined;

            // Map the correct date and template based on the stage type
            switch (templateType) {
              case "initial":
                scheduleDate = scheduleData.initial_date ? new Date(scheduleData.initial_date) : undefined;
                templateId = scheduleData.initial_template_id;
                break;
              case "reminder_1":
                scheduleDate = scheduleData.first_reminder_date ? new Date(scheduleData.first_reminder_date) : undefined;
                templateId = scheduleData.first_reminder_template_id;
                break;
              case "reminder_2":
                scheduleDate = scheduleData.second_reminder_date ? new Date(scheduleData.second_reminder_date) : undefined;
                templateId = scheduleData.second_reminder_template_id;
                break;
              case "reminder_4":
                scheduleDate = scheduleData.final_reminder_date ? new Date(scheduleData.final_reminder_date) : undefined;
                templateId = scheduleData.final_reminder_template_id;
                break;
            }

            // Update the stage if we found a schedule
            if (scheduleDate || templateId) {
              onChange({
                ...stage,
                date: scheduleDate,
                templateId: templateId,
              });
            }
          }
        }

        // If templates exist and no template is selected yet, set the default one
        if (templatesData && templatesData.length > 0 && !stage.templateId) {

          // Find the default template - either the one with "Default" in the name or the first one
          const defaultTemplate = templatesData.find(template =>
            template.name.includes('Default') ||
            template.name.includes('default') ||
            template.name.startsWith(t("templates.default_initial_email_name", "Default Initial Email")) ||
            template.name.startsWith(t("templates.default_first_reminder_name", "Default First Reminder")) ||
            template.name.startsWith(t("templates.default_second_reminder_name", "Default Second Reminder")) ||
            template.name.startsWith(t("templates.default_final_reminder_name", "Default Final Reminder"))
          );

          if (defaultTemplate) {
            onChange({ ...stage, templateId: defaultTemplate.id });
          } else if (templatesData.length > 0) {
            // If no default template is found, just use the first one
            onChange({ ...stage, templateId: templatesData[0].id });
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error(t("toasts.failed_load_schedule", "Failed to load schedule data"));
      } finally {
        setIsLoading(false);
      }
    };

    fetchTemplatesAndSchedule();
  }, [templateType, stage.templateId, onChange, invoiceId, t]);

  const isDateDisabled = (date: Date) => {
    if (previousStageDate) {
      // Disable dates that are on or before the previous stage's date
      return date < previousStageDate;
    }
    // For initial stage, only disable past dates
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return date < yesterday;
  };

  const handleDateChange = (date: Date | undefined) => {
    onChange({ ...stage, date });

    // If this is the initial stage and we have a date, notify parent to update other stages
    if (templateType === "initial" && date && onInitialDateChange) {
      onInitialDateChange(date);
    }
  };

  return (
    <div className="space-y-4 p-4 border rounded-lg bg-background">
      <div className="flex justify-between items-center">
        <h3 className="text-md font-bold text-[#34375B]">{title}</h3>
        {allowClear && stage.date && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onClear}
            className="text-xs font-semibold hover:bg-transparent p-0 text-[#34375B]/80 hover:text-[#34375B] flex items-center gap-1"
          >
            <span className="inline-block transform -translate-y-[0.75px]">x</span>
            <span> {t("common.clear")}</span>
          </Button>
        )}
      </div>

      <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
        <DateTimePicker
          date={stage.date}
          time={stage.time}
          onDateChange={handleDateChange}
          onTimeChange={(time) => onChange({ ...stage, time })}
          label="Send Date"
          isDateDisabled={isDateDisabled}
          useCurrentTimePlusFifteen={useCurrentTimePlusFifteen}
        />

        <div className="flex items-center gap-2 w-full md:w-auto">
          <Select
            value={stage.templateId}
            onValueChange={(value) => onChange({ ...stage, templateId: value })}
          >
            <SelectTrigger className="text-[#34375B] font-normal bg-white border border-gray-300 p-2 rounded-md w-full">
              <SelectValue placeholder={t("invoice_line_items.select_a_template", "Select a template")} />
            </SelectTrigger>
            <SelectContent className="bg-white border border-gray-300 p-2 rounded-md">
              {templates.map((template) => (
                <SelectItem key={template.id} value={template.id}>
                  {template.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            variant="outline"
            size="icon"
            className="bg-white text-[#636782] hover:text-[#34375B] hover:bg-white hover:shadow-[4px_4px_0px_0px_#34375B] hover:-translate-x-[1px] hover:-translate-y-[1px] transition-all duration-200 flex-shrink-0"
            onClick={handlePreviewClick}
            title={t("templates.preview_template", "Preview Template")}
          >
            <Eye size={18} />
          </Button>
        </div>
      </div>

      {/* Template Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>{selectedTemplate?.name || t("templates.preview", "Template Preview")}</DialogTitle>
          </DialogHeader>

          <div className="mt-4 border rounded-md p-4 max-h-[60vh] overflow-auto">
            {selectedTemplate && (
              <div
                className="prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{
                  __html: replaceTemplateVariables(selectedTemplate.content)
                }}
              />
            )}
          </div>

          <div className="mt-4 text-sm text-muted-foreground">
            <p className="text-sm font-thin">{t("templates.preview_note")}</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
